import { FooterVariant } from '@/components/page-sections/page-footer/variants';
import { HeaderVariant } from '@/components/page-sections/page-header/variants';
import { normalizeSocialPlatform, normalizeSocialUrl } from '@/components/shared/social-media';
import type { SiteFooterSlotConfig, SiteHeaderSlotConfig } from '@/components/slot-settings/types';
import type { ButtonSettings, ColorSettings, FontSettings, SiteLogo } from '@/types/shared';

export const SITE_HISTORY_LIMIT = 50;

// Site-wide layout code per special page type. Applies to all blog/case_result
// pages; persisted as a site setting, so it rides the site history stack.
export interface SpecialPageLayoutCodes {
  caseResult: string | null;
  blog: string | null;
}

export interface SiteStyles {
  logo: SiteLogo | null;
  logoTagline: string | null;
  colors: ColorSettings | null;
  persistedCustomColors: ColorSettings | null;
  fonts: FontSettings | null;
  header: SiteHeaderSlotConfig | null;
  footer: SiteFooterSlotConfig | null;
  buttons: ButtonSettings | null;
  specialPageLayouts: SpecialPageLayoutCodes | null;
}

export interface PersistedSiteStylesHistory {
  historyPast: SiteStyles[];
  historyFuture: SiteStyles[];
  historyPastTargets: (string | null)[];
  historyFutureTargets: (string | null)[];
}

export const cloneStyles = (styles: SiteStyles): SiteStyles => ({
  logo: styles.logo ? JSON.parse(JSON.stringify(styles.logo)) : null,
  logoTagline: styles.logoTagline ?? null,
  colors: styles.colors ? { ...styles.colors } : null,
  persistedCustomColors: styles.persistedCustomColors ? { ...styles.persistedCustomColors } : null,
  fonts: styles.fonts ? JSON.parse(JSON.stringify(styles.fonts)) : null,
  header: styles.header ? JSON.parse(JSON.stringify(styles.header)) : null,
  footer: styles.footer ? JSON.parse(JSON.stringify(styles.footer)) : null,
  buttons: styles.buttons ? { ...styles.buttons } : null,
  specialPageLayouts: styles.specialPageLayouts ? { ...styles.specialPageLayouts } : null,
});

export const trimSiteHistory = (history: SiteStyles[]): SiteStyles[] =>
  history.length > SITE_HISTORY_LIMIT
    ? history.slice(history.length - SITE_HISTORY_LIMIT)
    : history;

export const computeJsonChanged = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);

export const normalizeSiteHeader = (
  header: Partial<SiteHeaderSlotConfig> | null | undefined,
): SiteHeaderSlotConfig | null => {
  if (!header) return null;
  return {
    variant: header.variant ?? HeaderVariant.NAV_LEFT,
    elements: {
      showLogo: header.elements?.showLogo ?? true,
      showPrimaryButton: header.elements?.showPrimaryButton ?? true,
      showSecondaryButton: header.elements?.showSecondaryButton ?? true,
    },
    settings: {
      sticky: header.settings?.sticky ?? false,
      fullWidth: header.settings?.fullWidth ?? false,
      spacing: header.settings?.spacing ?? 'medium',
    },
    primaryButton: header.primaryButton,
    secondaryButton: header.secondaryButton,
  };
};

// Canonical footer config: same idea as header.
export const normalizeSiteFooter = (
  footer: Partial<SiteFooterSlotConfig> | null | undefined,
): SiteFooterSlotConfig | null => {
  if (!footer) return null;
  return {
    variant: footer.variant ?? FooterVariant.DEFAULT,
    elements: {
      showLogo: footer.elements?.showLogo ?? true,
      showText: footer.elements?.showText ?? true,
      showSocialLinks: footer.elements?.showSocialLinks ?? false,
      showNavigationLinks: footer.elements?.showNavigationLinks ?? true,
      showUniversalDisclaimer: footer.elements?.showUniversalDisclaimer ?? false,
    },
    settings: {
      fullWidth: footer.settings?.fullWidth ?? false,
      spacing: footer.settings?.spacing ?? 'medium',
    },
    socialLinks: (footer.socialLinks ?? []).map((icon) => ({
      platform: normalizeSocialPlatform(icon.platform) ?? icon.platform,
      url: normalizeSocialUrl(icon.url),
    })),
    text: footer.text,
    socialMediaHeading: footer.socialMediaHeading,
  };
};

export const emptySiteStylesHistory = (): PersistedSiteStylesHistory => ({
  historyPast: [],
  historyFuture: [],
  historyPastTargets: [],
  historyFutureTargets: [],
});

export const isEditorWindow =
  typeof globalThis === 'undefined' || typeof globalThis.window === 'undefined'
    ? false
    : (() => {
        try {
          const win = globalThis.window;
          return win.self === win.top;
        } catch {
          return false;
        }
      })();
